// The admin side of the content-order kit (plan R6 §2.1): home stats, success
// stories and company extras. Pure helpers, no React.
//
// A list is NEVER saved from a typed "Order" number or a client snapshot of
// rows: a move sends the WHOLE scope's ids in the new order plus the order the
// screen showed before the move (`expectedIds`); the server checks both. An
// item edit sends only the fields that changed, plus `expectedUpdatedAt`, and
// never `order`.
//
// The axios interceptor rejects with the API's error BODY (no status code):
// `{ error, message | messages, code?, detail? }`, or the string
// 'Something went wrong' when there was no answer at all.

export type MoveKind = 'up' | 'down' | 'top' | 'bottom';

export type Identified = { _id?: string };

export type ContentKind = 'homeStat' | 'successStory' | 'companyExtra';

// ---- texts (English UI; the server's own sentence is never shown for these) --

export const ORDER_CHANGED_TEXT =
  'This list was changed somewhere else. It has been reloaded — make your move again.';
export const ORDER_FAILED_TEXT = 'Could not save the new order. Nothing was changed.';
export const ITEM_CHANGED_TEXT =
  'Someone changed this item after you opened it. The latest version is now in the form — check it and save again.';
export const BUILT_IN_DELETE_TEXT =
  'Built-in items can be hidden, not deleted. Turn Active off to hide it.';
export const BUILT_IN_SECTION_TEXT = 'Built-in items stay in their section.';
export const SAVING_ORDER_TEXT = 'Saving order…';

// ---- moves -------------------------------------------------------------------

/** The index a move lands on (the same index when it cannot move). */
export function targetIndex(kind: MoveKind, from: number, length: number): number {
  if (!Number.isInteger(from) || from < 0 || from >= length) return from;
  if (kind === 'up') return Math.max(0, from - 1);
  if (kind === 'down') return Math.min(length - 1, from + 1);
  if (kind === 'top') return 0;
  return length - 1;
}

/** A new array with the item at `from` moved to `to`; an unchanged copy when either is out of range. */
export function moveTo<T>(list: readonly T[], from: number, to: number): T[] {
  const next = [...list];
  const inRange = (i: number) => Number.isInteger(i) && i >= 0 && i < list.length;
  if (!inRange(from) || !inRange(to) || from === to) return next;
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/** `moveTo` by a move kind. */
export function moveBy<T>(list: readonly T[], from: number, kind: MoveKind): T[] {
  return moveTo(list, from, targetIndex(kind, from, list.length));
}

/** True when the item at `index` can move that way. */
export function canMove(kind: MoveKind, index: number, length: number): boolean {
  return targetIndex(kind, index, length) !== index;
}

export function idsOf(rows: readonly Identified[]): string[] {
  return (rows || []).map((r) => String(r?._id ?? ''));
}

/** The exact pre-move list, as a new array (a failed save puts it back on screen). */
export function rollback<T>(before: readonly T[]): T[] {
  return [...before];
}

/**
 * Puts a scope's rows back into the full list: the scope's slots keep their
 * places and take the scope rows in their new order; rows outside the scope
 * are untouched. Scope rows the full list does not hold yet are appended.
 */
export function mergeScope<T extends Identified>(
  all: readonly T[],
  inScope: (row: T) => boolean,
  scopeRows: readonly T[]
): T[] {
  const queue = [...scopeRows];
  const out: T[] = [];
  all.forEach((row) => {
    if (!inScope(row)) out.push(row);
    else if (queue.length) out.push(queue.shift() as T);
  });
  return [...out, ...queue];
}

/** The PUT /reorder body: the scope's ids in the new order + the order the screen showed. */
export function reorderBody(
  before: readonly Identified[],
  after: readonly Identified[],
  scope: Record<string, unknown> = {}
): Record<string, unknown> {
  return { ...scope, ids: idsOf(after), expectedIds: idsOf(before) };
}

/** The fresh scope from a reorder answer `{ data: { items } }`, or null. */
export function itemsFromAnswer<T>(res: any): T[] | null {
  const body = res?.data?.data ?? res?.data;
  return Array.isArray(body?.items) ? (body.items as T[]) : null;
}

/** 1-based position of every row inside its group (the "#" column of a mixed view). */
export function positionsBy<T extends Identified>(
  rows: readonly T[],
  groupOf: (row: T) => string
): Map<string, number> {
  const seen = new Map<string, number>();
  const out = new Map<string, number>();
  rows.forEach((r) => {
    const g = groupOf(r);
    const n = (seen.get(g) || 0) + 1;
    seen.set(g, n);
    out.set(String(r._id), n);
  });
  return out;
}

// ---- item edits --------------------------------------------------------------

// Never part of an edit body: positions come from the reorder route only, and
// the rest is the server's own bookkeeping.
const NEVER_EDITED = new Set([
  '_id',
  'id',
  '__v',
  'order',
  'createdAt',
  'updatedAt',
  'builtInKey',
  'expectedUpdatedAt',
]);

const norm = (v: unknown): unknown => {
  if (v === undefined || v === null) return null;
  if (typeof v === 'string') return v.trim() === '' ? null : v.trim();
  if (Array.isArray(v)) return JSON.stringify(v.map(norm));
  if (typeof v === 'object') return JSON.stringify(v);
  return v;
};

const clean = (v: unknown): unknown => (typeof v === 'string' ? v.trim() : v);

/**
 * The fields of `edited` whose value differs from `original` (strings trimmed,
 * '' = missing, arrays and objects compared by value). `order` is never included.
 */
export function changedFields(
  original: Record<string, any>,
  edited: Record<string, any>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  Object.keys(edited || {}).forEach((k) => {
    if (NEVER_EDITED.has(k)) return;
    if (norm(edited[k]) !== norm((original || {})[k])) out[k] = clean(edited[k]);
  });
  return out;
}

/**
 * The PATCH body of an item edit: the changed fields, the version token, and
 * the identity keys an OLDER API's PartialType initialisers would otherwise
 * reset (critic C1): `section` for a company extra (else it moves to the partner
 * wall), `name` + `story` for a success story (else every edit is a 400).
 */
export function patchBody(
  kind: ContentKind,
  original: Record<string, any>,
  edited: Record<string, any>
): Record<string, unknown> {
  const body = changedFields(original, edited);
  if (kind === 'companyExtra') body.section = edited.section ?? original.section;
  if (kind === 'successStory') {
    body.name = clean(edited.name ?? original.name ?? '');
    body.story = clean(edited.story ?? original.story ?? '');
  }
  if (typeof original?.updatedAt === 'string' && original.updatedAt) {
    body.expectedUpdatedAt = original.updatedAt;
  }
  return body;
}

// ---- errors ------------------------------------------------------------------

export function errorCode(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : '';
}

const messagesOf = (err: unknown): string[] => {
  const m = (err as { messages?: unknown } | null)?.messages;
  return Array.isArray(m) ? m.filter((x): x is string => typeof x === 'string' && !!x) : [];
};

/** The text above the list after a failed move. */
export function orderErrorText(err: unknown): string {
  return errorCode(err) === 'ContentOrderChanged' ? ORDER_CHANGED_TEXT : ORDER_FAILED_TEXT;
}

export const isOrderChanged = (err: unknown) => errorCode(err) === 'ContentOrderChanged';
export const isItemChanged = (err: unknown) => errorCode(err) === 'ContentItemChanged';

/** The row a 409 ContentItemChanged carries (the latest version), or null. */
export function itemFromConflict<T>(err: unknown): T | null {
  if (!isItemChanged(err)) return null;
  const item = (err as { detail?: { item?: unknown } }).detail?.item;
  return item && typeof item === 'object' ? (item as unknown as T) : null;
}

/** The text of a failed item save (create or edit). */
export function itemErrorText(err: unknown, fallback = 'Save failed'): string {
  const code = errorCode(err);
  if (code === 'ContentItemChanged') return ITEM_CHANGED_TEXT;
  if (code === 'CompanyExtraBuiltIn') return BUILT_IN_SECTION_TEXT;
  const messages = messagesOf(err);
  if (messages.length) return messages.join(' · ');
  const message = (err as { message?: unknown } | null)?.message;
  if (typeof message === 'string' && message) return message;
  if (Array.isArray(message) && message.length) return message.join(' · ');
  return fallback;
}

/** The text of a failed delete. */
export function deleteErrorText(err: unknown, fallback = 'Delete failed'): string {
  if (errorCode(err) === 'CompanyExtraBuiltIn') return BUILT_IN_DELETE_TEXT;
  const message = (err as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message ? message : fallback;
}

/**
 * Validation messages that start with one of `fields` (class-validator writes
 * "name must be longer than…"), so the form can put each on its field.
 */
export function fieldErrors(
  err: unknown,
  fields: readonly string[]
): { field: string; message: string }[] {
  const known = new Set(fields);
  return messagesOf(err)
    .map((message) => ({ field: (/^([A-Za-z_]+)/.exec(message) || [])[1] || '', message }))
    .filter((x) => known.has(x.field));
}
